// Components/Pages/Admin/Media.jsx
import { useState, useEffect } from "react";
import AdminTable from "./AdminTable";
import AdminForm from "./AdminForm";
import { api } from "./api";
import { FaYoutube } from "react-icons/fa";

const Media = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const data = await api.getMedia();
      setVideos(data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingVideo(null);
    setShowForm(true);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await api.deleteMedia(id);
        await fetchMedia();
      } catch (error) {
        console.error('Error deleting video:', error);
      }
    }
  };

  const handleSubmit = async (data) => {
    try {
      // Extract YouTube video ID from URL
      const youtubeId = extractYouTubeId(data.youtube_url);
      
      // Auto-generate thumbnail from YouTube ID
      const thumbnail = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : '';
      
      const videoData = {
        title: data.title,
        description: data.description || '',
        youtube_url: data.youtube_url,
        thumbnail: thumbnail,
        duration: data.duration || '',
        views: data.views || '',
        date: data.date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      };

      if (editingVideo) {
        await api.updateMedia(editingVideo.id, videoData);
      } else {
        await api.createMedia(videoData);
      }
      setShowForm(false);
      await fetchMedia();
    } catch (error) {
      console.error('Error saving video:', error);
    }
  };

  // Helper function to extract YouTube video ID
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Auto-fill duration and views from YouTube (optional - you can remove this)
  const fetchYouTubeData = async (url) => {
    const videoId = extractYouTubeId(url);
    if (!videoId) return;
    
    try {
      // You can use YouTube API here if you have an API key
      // For now, we'll just set the thumbnail
      const thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      return { thumbnail };
    } catch (error) {
      console.error('Error fetching YouTube data:', error);
      return null;
    }
  };

  const columns = [
    { 
      key: 'thumbnail', 
      label: 'Thumbnail', 
      render: (val) => val ? (
        <img src={val} alt="Thumbnail" className="h-12 w-20 object-cover rounded" />
      ) : (
        <FaYoutube className="text-2xl text-red-500" />
      )
    },
    { key: 'title', label: 'Title' },
    { 
      key: 'youtube_url', 
      label: 'YouTube Link', 
      render: (val) => val ? (
        <a href={val} target="_blank" rel="noopener noreferrer" className="text-primaryGold hover:text-yellow-600 text-sm">
          Watch Video
        </a>
      ) : 'No URL'
    },
    { key: 'description', label: 'Description', render: (val) => val?.substring(0, 60) + (val?.length > 60 ? '...' : '') },
    { key: 'date', label: 'Date' },
  ];

  const fields = [
    { 
      name: 'title', 
      label: 'Title', 
      required: true, 
      placeholder: 'Enter video title e.g., Project Launch: CLEAN Project' 
    },
    { 
      name: 'description', 
      label: 'Description (max 40 words)', 
      type: 'textarea', 
      required: false, 
      rows: 3,
      placeholder: 'Brief description of the video (max 40 words)...',
      help: 'Maximum 40 words'
    },
    { 
      name: 'youtube_url', 
      label: 'YouTube URL', 
      required: true, 
      placeholder: 'https://www.youtube.com/watch?v=VIDEO_ID',
      help: 'Thumbnail will be automatically generated from the YouTube video'
    },
    { 
      name: 'date', 
      label: 'Date', 
      required: false, 
      placeholder: 'e.g., December 2024',
      help: 'Leave blank to use current date'
    },
  ];

  // Word count helper for description
  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Validate description word count
  const validateDescription = (text) => {
    if (!text) return true;
    return getWordCount(text) <= 40;
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <AdminTable
        title="Media Videos"
        columns={columns}
        data={videos}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        addButtonLabel="Add Video"
      />

      {showForm && (
        <AdminForm
          title="Media Video"
          fields={fields}
          initialData={editingVideo || {}}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isEditing={!!editingVideo}
          validateForm={(data) => {
            if (data.description && !validateDescription(data.description)) {
              alert('Description cannot exceed 40 words');
              return false;
            }
            return true;
          }}
        />
      )}
    </div>
  );
};

export default Media;